'use client';

import { useEffect, useRef, useState } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { mountPreviewVideo } from './preview-video-lifecycle';

type PreviewVideoProps = {
  src: string;
  poster?: string;
  sizes: string;
  /** Načíta len metadáta a prvý snímok (bez prehrávania), aby bol slide viditeľný aj mimo prehrávania. */
  showFirstFrame?: boolean;
  className?: string;
  overlayClassName?: string;
};

export function PreviewVideo({
  src,
  poster,
  sizes,
  showFirstFrame = false,
  className,
  overlayClassName,
}: PreviewVideoProps) {
  const { t } = useTranslations();
  const videoRef = useRef<HTMLVideoElement>(null);
  const lifecycle = useRef<ReturnType<typeof mountPreviewVideo> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setHasFrame(false);
    const controller = mountPreviewVideo(video, src);
    lifecycle.current = controller;
    return () => {
      controller.dispose();
      lifecycle.current = null;
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showFirstFrame || video.getAttribute('src')) return;
    // Fragment #t zaručí zobrazenie prvého snímku aj v Safari; sťahuje sa len začiatok súboru.
    video.preload = 'metadata';
    video.setAttribute('src', `${src}#t=0.001`);
  }, [showFirstFrame, src]);

  return (
    <button
      type="button"
      className={cn(
        'relative block h-full w-full cursor-pointer overflow-hidden bg-black',
        className,
      )}
      onClick={() => lifecycle.current?.toggle()}
      aria-label={isPlaying ? t('text4064') : t('text2335')}
    >
      {poster && !hasFrame && (
        <Image
          src={poster}
          alt=""
          width={0}
          height={0}
          sizes={sizes}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
      )}
      <video
        ref={videoRef}
        preload="none"
        playsInline
        muted
        loop
        aria-hidden="true"
        className={cn(
          'absolute inset-0 h-full w-full object-cover object-center',
          !hasFrame && 'opacity-0',
        )}
        onLoadedData={() => setHasFrame(true)}
        onPlaying={() => {
          setIsPlaying(true);
          setHasFrame(true);
        }}
        onPause={() => setIsPlaying(false)}
        onError={() => setIsPlaying(false)}
      />
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center transition-opacity duration-300',
          overlayClassName,
          isPlaying ? 'pointer-events-none opacity-0' : 'opacity-100',
        )}
      >
        <Image src="/images/play.svg" alt="" width={128} height={128} className="object-contain" />
      </span>
    </button>
  );
}
